import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { getEscandalloSubgruposMuestras } from "../../api/Muestras/muestraApi";
import { insertEstimateRfqQuoteLines } from "../../api/Estimates/estimateRfqApi";
import {
  RFQ_QUOTE_CURRENCIES,
  RFQ_QUOTE_GROUPS,
  emptyRfqQuoteRow,
  rowsFromStoredOrDefaults,
  parseRfqQuoteLinesJson,
  sumLineTotalsByCurrency,
  lineExtendedTotal,
  lineWeightPct,
  formatLineExtendedLabel,
} from "../../constants/rfqQuoteGroups";

export { RFQ_QUOTE_GROUPS };

const BRAND = "#00c2a3";
const MAX_LINES = 30;

function formatMoney(value, currency) {
  const n = Number(String(value).replace(",", "."));
  if (!Number.isFinite(n)) return "—";
  try {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: currency || "EUR",
      maximumFractionDigits: 4,
    }).format(n);
  } catch {
    return `${n} ${currency || ""}`.trim();
  }
}

/**
 * Registra cotización del proveedor (líneas dinámicas) e inserta en escandallo.
 * Prefills desde QuoteLinesJson del portal si existe.
 */
export default function EstimateInsertarCotizacionLineasDialog({
  open,
  onClose,
  rfq,
  onApplied,
}) {
  const [rows, setRows] = useState(() =>
    RFQ_QUOTE_GROUPS.map((g) => emptyRfqQuoteRow(g.grupoId)),
  );
  const [leadTime, setLeadTime] = useState("");
  const [notes, setNotes] = useState("");
  const [subgruposByGrupo, setSubgruposByGrupo] = useState({});
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState("");

  const portalLines = useMemo(
    () => parseRfqQuoteLinesJson(rfq?.QuoteLinesJson),
    [rfq?.QuoteLinesJson],
  );
  const fromPortal = portalLines.length > 0;

  useEffect(() => {
    if (!open || !rfq) return;
    setLeadTime(rfq.QuotedLeadTime || "");
    setNotes(rfq.QuotedNotes || "");
    setError("");
    setConfirmOpen(false);
    setRows(
      rowsFromStoredOrDefaults(portalLines, rfq.QuotedCurrency || "EUR"),
    );

    let cancelled = false;
    const loadSubs = async () => {
      setLoadingSubs(true);
      try {
        const entries = await Promise.all(
          RFQ_QUOTE_GROUPS.map(async (g) => {
            const list = await getEscandalloSubgruposMuestras(g.grupoId);
            return [g.grupoId, Array.isArray(list) ? list : []];
          }),
        );
        if (cancelled) return;
        const map = Object.fromEntries(entries);
        setSubgruposByGrupo(map);
        setRows((prev) =>
          prev.map((row) => {
            if (row.subgrupoId) return row;
            const list = map[row.grupoId] || [];
            if (list.length === 1) {
              return {
                ...row,
                subgrupoId: Number(list[0].ID_SUBGRUPO),
              };
            }
            return row;
          }),
        );
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.error ||
              err?.message ||
              "No se pudieron cargar subgrupos",
          );
        }
      } finally {
        if (!cancelled) setLoadingSubs(false);
      }
    };
    loadSubs();
    return () => {
      cancelled = true;
    };
  }, [open, rfq, portalLines]);

  const proveedorLabel = useMemo(() => {
    if (!rfq) return "—";
    return (
      rfq.ProveedorNombre ||
      (rfq.CodProv != null ? `COD ${rfq.CodProv}` : "—")
    );
  }, [rfq]);

  const filledRows = useMemo(() => {
    return rows.filter((row) => String(row.coste).trim() !== "");
  }, [rows]);

  const totalsByMon = useMemo(() => sumLineTotalsByCurrency(rows), [rows]);

  const summaryLabel = useMemo(() => {
    if (!filledRows.length) return "Ninguna línea con coste";
    const currencies = [
      ...new Set(filledRows.map((r) => String(r.moneda || "EUR").toUpperCase())),
    ];
    if (currencies.length === 1) {
      const total = filledRows.reduce((acc, r) => {
        const ext = lineExtendedTotal(r);
        return acc + (ext != null ? ext : 0);
      }, 0);
      return `${filledRows.length} línea${filledRows.length === 1 ? "" : "s"} · suma ${formatMoney(total, currencies[0])}`;
    }
    return `${filledRows.length} línea${filledRows.length === 1 ? "" : "s"} con coste`;
  }, [filledRows]);

  const updateRow = (key, patch) => {
    setRows((prev) =>
      prev.map((r) => (r.key === key ? { ...r, ...patch } : r)),
    );
  };

  const removeRow = (key) => {
    setRows((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((r) => r.key !== key);
    });
  };

  const addRow = () => {
    setRows((prev) => {
      if (prev.length >= MAX_LINES) return prev;
      return [...prev, emptyRfqQuoteRow(RFQ_QUOTE_GROUPS[0].grupoId)];
    });
  };

  const buildPayloadLines = () => {
    const payloadLines = [];
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const costRaw = String(row.coste).trim();
      const hasSub = Boolean(row.subgrupoId);
      if (!costRaw && !hasSub) continue;
      const cost = Number(String(row.coste).replace(",", "."));
      const gLabel =
        RFQ_QUOTE_GROUPS.find((g) => g.grupoId === row.grupoId)?.label ||
        `línea ${i + 1}`;
      if (!costRaw || !Number.isFinite(cost) || cost < 0) {
        setError(`Coste inválido en ${gLabel}`);
        return null;
      }
      if (!row.subgrupoId) {
        setError(`Elige subgrupo en ${gLabel}`);
        return null;
      }
      const un = Number(String(row.un).replace(",", "."));
      if (!Number.isFinite(un) || un <= 0) {
        setError(`Units inválidas en ${gLabel}`);
        return null;
      }
      payloadLines.push({
        grupoId: row.grupoId,
        subgrupoId: Number(row.subgrupoId),
        un,
        coste: cost,
        moneda: row.moneda || "EUR",
      });
    }

    if (!payloadLines.length) {
      setError(
        "Rellena al menos una línea con coste (puedes dejar el resto vacías).",
      );
      return null;
    }
    return payloadLines;
  };

  const handleAskConfirm = () => {
    setError("");
    const payload = buildPayloadLines();
    if (!payload) return;
    setConfirmOpen(true);
  };

  const handleSave = async () => {
    const payloadLines = buildPayloadLines();
    if (!payloadLines) {
      setConfirmOpen(false);
      return;
    }

    setSaving(true);
    setError("");
    try {
      const result = await insertEstimateRfqQuoteLines(rfq.IdEstimateRfq, {
        lines: payloadLines,
        quotedLeadTime: leadTime,
        quotedNotes: notes,
      });
      setConfirmOpen(false);
      onApplied?.(result);
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "No se pudo insertar la cotización",
      );
      setConfirmOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={saving ? undefined : onClose}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: { borderRadius: 2.5, overflow: "hidden" },
        }}
      >
        <DialogTitle sx={{ pb: 1.25 }}>
          <Stack spacing={0.75}>
            <Typography variant="h6" fontWeight={800} lineHeight={1.25}>
              {fromPortal ? "Revisar cotización del proveedor" : "Registrar cotización"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Se crearán líneas en el escandallo. Las filas sin coste se ignoran.
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={2}>
            {error ? <Alert severity="error">{error}</Alert> : null}

            <Box
              sx={{
                p: 1.75,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "grey.50",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ sm: "center" }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={700}
                    sx={{ letterSpacing: 0.4 }}
                  >
                    PROVEEDOR
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={800}>
                    {proveedorLabel}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    RFQ #{rfq?.IdEstimateRfq || "—"}
                    {rfq?.Estado ? ` · ${rfq.Estado}` : ""}
                    {rfq?.ToEmail ? ` · ${rfq.ToEmail}` : ""}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {fromPortal ? (
                    <Chip
                      size="small"
                      label="Desde portal"
                      sx={{
                        bgcolor: "rgba(0,194,163,0.12)",
                        color: "#067a68",
                        fontWeight: 700,
                      }}
                    />
                  ) : (
                    <Chip size="small" label="Entrada manual" variant="outlined" />
                  )}
                  <Chip
                    size="small"
                    label={summaryLabel}
                    variant="outlined"
                    sx={{ fontWeight: 600 }}
                  />
                </Stack>
              </Stack>
            </Box>

            {fromPortal ? (
              <Alert severity="success" sx={{ py: 0.75 }}>
                Cotización recibida del portal. Revisa subgrupos y costes, luego
                confirma la inserción en escandallo.
              </Alert>
            ) : (
              <Alert severity="info" sx={{ py: 0.75 }}>
                No hace falta crear líneas antes. Rellena solo los grupos con
                coste del proveedor.
              </Alert>
            )}

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                label="Lead time"
                value={leadTime}
                onChange={(e) => setLeadTime(e.target.value)}
                placeholder="p.ej. 45 days"
                fullWidth
                size="small"
              />
              <TextField
                label="Notas"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                fullWidth
                size="small"
              />
            </Stack>

            {loadingSubs ? (
              <Stack alignItems="center" py={4}>
                <CircularProgress size={28} />
              </Stack>
            ) : (
              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary">
                  Puedes añadir o quitar líneas. Las vacías se ignoran al insertar.
                </Typography>
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      display: { xs: "none", md: "grid" },
                      gridTemplateColumns:
                        "minmax(140px,1.1fr) minmax(150px,1.4fr) 72px 84px 100px 88px 72px 44px",
                      gap: 1,
                      px: 1.5,
                      py: 1,
                      bgcolor: "grey.50",
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    {["Grupo", "Subgrupo", "Units", "Moneda", "Coste", "Total", "Peso", ""].map(
                      (h, i) => (
                        <Typography
                          key={`${h}-${i}`}
                          variant="caption"
                          fontWeight={800}
                          color="text.secondary"
                          sx={{ letterSpacing: 0.35 }}
                        >
                          {h}
                        </Typography>
                      ),
                    )}
                  </Box>

                  <Stack divider={<Divider flexItem />}>
                    {rows.map((row) => {
                      const subs = subgruposByGrupo[row.grupoId] || [];
                      const selectedSub =
                        subs.find(
                          (s) =>
                            Number(s.ID_SUBGRUPO) === Number(row.subgrupoId),
                        ) || null;
                      const hasCost = String(row.coste).trim() !== "";
                      const gLabel =
                        RFQ_QUOTE_GROUPS.find((g) => g.grupoId === row.grupoId)
                          ?.label || "—";
                      const weight = lineWeightPct(row, totalsByMon);
                      return (
                        <Box
                          key={row.key}
                          sx={{
                            px: 1.5,
                            py: 1.25,
                            bgcolor: hasCost
                              ? "rgba(0,194,163,0.04)"
                              : "background.paper",
                            borderLeft: "3px solid",
                            borderLeftColor: hasCost ? BRAND : "transparent",
                          }}
                        >
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: {
                                xs: "1fr",
                                md: "minmax(140px,1.1fr) minmax(150px,1.4fr) 72px 84px 100px 88px 72px 44px",
                              },
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <TextField
                              select
                              label="Grupo"
                              size="small"
                              value={row.grupoId}
                              onChange={(e) =>
                                updateRow(row.key, {
                                  grupoId: Number(e.target.value),
                                  subgrupoId: "",
                                })
                              }
                            >
                              {RFQ_QUOTE_GROUPS.map((g) => (
                                <MenuItem key={g.grupoId} value={g.grupoId}>
                                  {g.label}
                                </MenuItem>
                              ))}
                            </TextField>

                            <Autocomplete
                              size="small"
                              options={subs}
                              value={selectedSub}
                              getOptionLabel={(o) => o?.SUBGRUPO || ""}
                              isOptionEqualToValue={(a, b) =>
                                Number(a?.ID_SUBGRUPO) ===
                                Number(b?.ID_SUBGRUPO)
                              }
                              onChange={(_e, val) =>
                                updateRow(row.key, {
                                  subgrupoId: val
                                    ? Number(val.ID_SUBGRUPO)
                                    : "",
                                })
                              }
                              renderInput={(params) => (
                                <TextField {...params} label="Subgrupo" />
                              )}
                            />
                            <TextField
                              label="Units"
                              value={row.un}
                              onChange={(e) =>
                                updateRow(row.key, { un: e.target.value })
                              }
                              size="small"
                            />
                            <TextField
                              select
                              label="Moneda"
                              value={row.moneda}
                              onChange={(e) =>
                                updateRow(row.key, { moneda: e.target.value })
                              }
                              size="small"
                            >
                              {RFQ_QUOTE_CURRENCIES.map((c) => (
                                <MenuItem key={c} value={c}>
                                  {c}
                                </MenuItem>
                              ))}
                            </TextField>
                            <TextField
                              label="Coste"
                              value={row.coste}
                              onChange={(e) =>
                                updateRow(row.key, { coste: e.target.value })
                              }
                              size="small"
                              inputProps={{ inputMode: "decimal" }}
                            />
                            <Box
                              sx={{
                                px: 1,
                                py: 1,
                                borderRadius: 1.25,
                                bgcolor: hasCost ? "rgba(0,194,163,0.06)" : "grey.50",
                                border: "1px solid",
                                borderColor: hasCost
                                  ? "rgba(0,194,163,0.35)"
                                  : "divider",
                                textAlign: "right",
                                minHeight: 40,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                              }}
                              title="Units × coste"
                            >
                              <Typography
                                fontWeight={800}
                                sx={{
                                  color: hasCost ? "#067a68" : "text.disabled",
                                  fontVariantNumeric: "tabular-nums",
                                }}
                              >
                                {formatLineExtendedLabel(row)}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                px: 1,
                                py: 1,
                                borderRadius: 1.25,
                                bgcolor: hasCost ? "rgba(0,194,163,0.1)" : "grey.50",
                                border: "1px solid",
                                borderColor: hasCost
                                  ? "rgba(0,194,163,0.35)"
                                  : "divider",
                                textAlign: "right",
                                minHeight: 40,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                              }}
                              title="Peso del coste sobre el total"
                            >
                              <Typography
                                fontWeight={800}
                                sx={{
                                  color: hasCost ? "#067a68" : "text.disabled",
                                  fontVariantNumeric: "tabular-nums",
                                }}
                              >
                                {weight}
                              </Typography>
                            </Box>
                            <Button
                              onClick={() => removeRow(row.key)}
                              disabled={rows.length <= 1}
                              size="small"
                              sx={{ minWidth: 40, color: "text.secondary" }}
                              title={`Quitar ${gLabel}`}
                            >
                              ×
                            </Button>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
                <Button
                  variant="outlined"
                  onClick={addRow}
                  disabled={rows.length >= MAX_LINES}
                  sx={{
                    alignSelf: "stretch",
                    borderStyle: "dashed",
                    borderColor: BRAND,
                    color: BRAND,
                    fontWeight: 700,
                  }}
                >
                  + Añadir línea
                </Button>
              </Stack>
            )}
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 420 }}>
            Proveedor fijo en todas las líneas: <strong>{proveedorLabel}</strong>
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button onClick={onClose} disabled={saving}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleAskConfirm}
              disabled={saving || loadingSubs || !filledRows.length}
              sx={{
                bgcolor: BRAND,
                fontWeight: 800,
                px: 2.5,
                "&:hover": { bgcolor: "#00a88c" },
              }}
            >
              Insertar en escandallo
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmOpen}
        onClose={saving ? undefined : () => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>¿Insertar en escandallo?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Se crearán o actualizarán líneas de presupuesto con el proveedor{" "}
            <strong>{proveedorLabel}</strong>. Esta acción marcará la RFQ como{" "}
            <strong>Applied</strong>.
          </Typography>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1.5,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="body2" fontWeight={700}>
              {summaryLabel}
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              {filledRows.map((r) => {
                const g = RFQ_QUOTE_GROUPS.find((x) => x.grupoId === r.grupoId);
                const subs = subgruposByGrupo[r.grupoId] || [];
                const sub =
                  subs.find(
                    (s) => Number(s.ID_SUBGRUPO) === Number(r.subgrupoId),
                  )?.SUBGRUPO || `#${r.subgrupoId}`;
                return (
                  <Typography
                    key={r.key}
                    variant="caption"
                    color="text.secondary"
                  >
                    {g?.label}: {sub} · {r.un} × {formatMoney(r.coste, r.moneda)} ={" "}
                    {formatLineExtendedLabel(r)} ·{" "}
                    {lineWeightPct(r, totalsByMon)}
                  </Typography>
                );
              })}
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={saving}>
            Revisar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={
              saving ? <CircularProgress size={16} color="inherit" /> : null
            }
            sx={{
              bgcolor: BRAND,
              fontWeight: 800,
              "&:hover": { bgcolor: "#00a88c" },
            }}
          >
            Confirmar inserción
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
